import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FC, ReactNode, memo, useEffect, useMemo, useState } from 'react';
import { ShipmentOrderLegRunItemEntity } from 'src/entities/ShipmentOrderLegRun.entity';

import {
  SplitRow,
  buildInitialRows,
  computeRemainders,
  gToKg,
  isConfirmable,
  kgToG,
  m3ToMm3,
  mm3ToM3,
  reshapeRows,
  toSplitPayload,
} from './splitAllocation';

type Step = 1 | 2 | 3;

type Props = {
  open: boolean;
  legRunId: number;
  items: ShipmentOrderLegRunItemEntity[];
  invoiceNo?: string | null;
  storeName?: string | null;
  scheduledDate?: string | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (legRunId: number, splits: ReturnType<typeof toSplitPayload>) => void;
};

const MIN_SPLIT = 2;
const MAX_SPLIT = 10;

const ShipmentOrderLegRunSplitForm: FC<Props> = memo(({
  open,
  legRunId,
  items,
  invoiceNo,
  storeName,
  scheduledDate,
  loading,
  onClose,
  onConfirm,
}) => {
  const [step, setStep] = useState<Step>(1);
  const [splitCount, setSplitCount] = useState(MIN_SPLIT);
  const [rows, setRows] = useState<SplitRow[]>([]);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSplitCount(MIN_SPLIT);
    setRows(buildInitialRows(items, MIN_SPLIT));
  }, [open, items]);

  const remainders = useMemo(() => computeRemainders(rows), [rows]);
  const confirmable = useMemo(() => isConfirmable(rows, splitCount), [rows, splitCount]);

  const updateAlloc = (
    rowIdx: number,
    target: number,
    field: 'quantity' | 'weightG' | 'volumeMm3',
    value: number,
  ) => {
    setRows((prev) => prev.map((row, i) => (
      i === rowIdx
        ? { ...row, alloc: row.alloc.map((a, j) => (j === target ? { ...a, [field]: value } : a)) }
        : row
    )));
  };

  const changeQuantity = (rowIdx: number, target: number, newQty: number) => {
    setRows((prev) => prev.map((row, i) => {
      if (i !== rowIdx) return row;
      const unitWeightG = row.origQuantity > 0 ? row.origWeightG / row.origQuantity : 0;
      const unitVolumeMm3 = row.origVolumeMm3 !== null && row.origQuantity > 0
        ? row.origVolumeMm3 / row.origQuantity
        : null;
      return {
        ...row,
        alloc: row.alloc.map((a, j) => (j === target
          ? {
            quantity: newQty,
            weightG: Math.round(unitWeightG * newQty),
            volumeMm3: unitVolumeMm3 === null ? null : Math.round(unitVolumeMm3 * newQty),
          }
          : a)),
      };
    }));
  };

  const distributeEvenly = (rowIdx: number) => {
    setRows((prev) => prev.map((row, i) => (i === rowIdx ? reshapeRows([row], splitCount)[0] : row)));
  };

  const distributeAllEvenly = () => setRows((prev) => reshapeRows(prev, splitCount));

  const dumpAllTo = (rowIdx: number, target: number) => {
    setRows((prev) => prev.map((row, i) => {
      if (i !== rowIdx) return row;
      // 数量1の商品は各分割先で数量1固定。全量をここへ では重量・体積のみ target へ集約し、数量は崩さない。
      const fixedQty1 = row.origQuantity === 1;
      return {
        ...row,
        alloc: row.alloc.map((_, j) =>
          j === target
            ? { quantity: row.origQuantity, weightG: row.origWeightG, volumeMm3: row.origVolumeMm3 }
            : { quantity: fixedQty1 ? 1 : 0, weightG: 0, volumeMm3: row.origVolumeMm3 === null ? null : 0 },
        ),
      };
    }));
  };

  const handleConfirm = () => onConfirm(legRunId, toSplitPayload(rows, splitCount));

  const totalOrigQty = rows.reduce((s, r) => s + r.origQuantity, 0);
  const totalOrigWeightG = rows.reduce((s, r) => s + r.origWeightG, 0);
  const totalOrigVolumeMm3 = rows.reduce((s, r) => s + (r.origVolumeMm3 ?? 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>伝票を分割</Typography>
          <Typography variant="caption">Step {step} / 3</Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}><CloseRoundedIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {step === 1 && (
          <Stack spacing={2}>
            <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
              <Stack spacing={0.75}>
                <InfoRow label="伝票番号" value={invoiceNo ?? '—'} />
                <InfoRow label="店舗名" value={storeName ?? '—'} />
                <InfoRow label="配送日" value={scheduledDate ?? '—'} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow label="数量合計" value={`${totalOrigQty} cs`} />
                <InfoRow label="重量合計" value={`${gToKg(totalOrigWeightG)} kg`} />
                <InfoRow label="体積合計" value={`${toM3(totalOrigVolumeMm3)} m³`} />
              </Stack>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography sx={{ fontWeight: 600 }}>分割数</Typography>
              <TextField
                type="number"
                size="small"
                value={splitCount}
                onChange={(e) => {
                  const num = Number(e.target.value);
                  if (Number.isNaN(num)) return;
                  const val = Math.min(MAX_SPLIT, num);
                  setSplitCount(val);
                  if (val >= MIN_SPLIT) {
                    setRows((prev) => reshapeRows(prev, val));
                  }
                }}
                onBlur={() => {
                  const val = Math.max(MIN_SPLIT, Math.min(MAX_SPLIT, splitCount));
                  setSplitCount(val);
                  setRows((prev) => reshapeRows(prev, val));
                }}
                slotProps={{ htmlInput: { min: MIN_SPLIT, max: MAX_SPLIT } }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{MIN_SPLIT}〜{MAX_SPLIT}の範囲で指定</Typography>
            </Stack>
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                各商品行を {splitCount} 個の分割先へ配分してください
              </Typography>
              <Button size="small" startIcon={<AutoFixHighRoundedIcon sx={{ fontSize: 14 }} />} onClick={distributeAllEvenly}>
                全行を均等割
              </Button>
            </Stack>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <Box component="thead" sx={{ bgcolor: 'action.hover' }}>
                    <Box component="tr">
                      <Th sx={{ minWidth: 180, textAlign: 'left' }}>商品 / 元数値</Th>
                      {Array.from({ length: splitCount }, (_, i) => (
                        <Th key={`split-${i + 1}`} sx={{ minWidth: 160 }}>分割先 {i + 1}</Th>
                      ))}
                      <Th sx={{ minWidth: 100 }}>残量</Th>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {rows.map((row, idx) => {
                      const rem = remainders[idx];
                      return (
                        <Box component="tr" key={`row-${row.shipmentOrderItemId}`} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                          <Td sx={{ verticalAlign: 'top' }}>
                            <Stack spacing={0.5}>
                              <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{row.name}</Typography>
                              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                数量 {row.origQuantity} / 重量 {gToKg(row.origWeightG)}kg / 体積 {row.origVolumeMm3 === null ? '—' : toM3(row.origVolumeMm3)}m³
                              </Typography>
                              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => distributeEvenly(idx)}
                                  sx={{ fontSize: 10, py: 0.25, px: 0.75, minWidth: 0 }}
                                >
                                  均等割
                                </Button>
                              </Stack>
                            </Stack>
                          </Td>
                          {row.alloc.map((a, j) => (
                            <Td key={`cell-${row.shipmentOrderItemId}-${j}`} sx={{ verticalAlign: 'top' }}>
                              <Stack spacing={0.5}>
                                <NumInput
                                  label="数"
                                  value={a.quantity}
                                  onChange={(v) => changeQuantity(idx, j, v)}
                                  disabled={row.origQuantity === 1}
                                />
                                <NumInput
                                  label="kg"
                                  value={gToKg(a.weightG) ?? 0}
                                  onChange={(v) => updateAlloc(idx, j, 'weightG', kgToG(v))}
                                  step={0.1}
                                />
                                {row.origVolumeMm3 !== null && (
                                  <NumInput
                                    label="m³"
                                    value={toM3(a.volumeMm3) ?? 0}
                                    onChange={(v) => updateAlloc(idx, j, 'volumeMm3', m3ToMm3(v))}
                                    step={0.001}
                                  />
                                )}
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => dumpAllTo(idx, j)}
                                  sx={{ fontSize: 10, py: 0, minWidth: 0 }}
                                >
                                  全量をここへ
                                </Button>
                              </Stack>
                            </Td>
                          ))}
                          <Td>
                            <Stack spacing={0.25}>
                              <RemText label="数" value={rem.quantity} isError={rem.quantity > 0} />
                              <RemText label="kg" value={gToKg(rem.weightG) ?? 0} isError={rem.weightG !== 0} />
                              <RemText label="m³" value={toM3(rem.volumeMm3) ?? 0} isError={rem.volumeMm3 !== 0} />
                            </Stack>
                          </Td>
                        </Box>
                      );
                    })}
                    <Box component="tr" sx={{ borderTop: '2px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.04)' }}>
                      <Td sx={{ verticalAlign: 'top' }}>
                        <Stack spacing={0.25}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>合計（全商品）</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            元: 数量 {totalOrigQty} / 重量 {gToKg(totalOrigWeightG)}kg / 体積 {toM3(totalOrigVolumeMm3)}m³
                          </Typography>
                        </Stack>
                      </Td>
                      {Array.from({ length: splitCount }, (_, j) => {
                        const colQty = rows.reduce((s, r) => s + (r.alloc[j]?.quantity || 0), 0);
                        const colWeight = rows.reduce((s, r) => s + (r.alloc[j]?.weightG || 0), 0);
                        const colVolume = rows.reduce((s, r) => s + (r.alloc[j]?.volumeMm3 ?? 0), 0);
                        return (
                          <Td key={`total-split-${j}`} sx={{ verticalAlign: 'top' }}>
                            <Stack spacing={0.25}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>数 {colQty}</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>kg {gToKg(colWeight)}</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>m³ {toM3(colVolume)}</Typography>
                            </Stack>
                          </Td>
                        );
                      })}
                      <Td>
                        <Stack spacing={0.25}>
                          {(() => {
                            const totalRemQty = remainders.reduce((s, r) => s + r.quantity, 0);
                            const totalRemWeight = remainders.reduce((s, r) => s + r.weightG, 0);
                            const totalRemVolume = remainders.reduce((s, r) => s + r.volumeMm3, 0);
                            return (
                              <>
                                <RemText label="数" value={totalRemQty} isError={totalRemQty > 0} />
                                <RemText label="kg" value={gToKg(totalRemWeight) ?? 0} isError={totalRemWeight !== 0} />
                                <RemText label="m³" value={toM3(totalRemVolume) ?? 0} isError={totalRemVolume !== 0} />
                              </>
                            );
                          })()}
                        </Stack>
                      </Td>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
                    <Typography
              color="error"
              sx={{ fontSize: 12, visibility: confirmable ? 'hidden' : 'visible', minHeight: 18 }}
            >
              各商品の重量・体積の合計が元と一致し（数量2以上の商品は数量も一致）、すべての分割先に値が入るまで保存できません。
            </Typography>
          </Stack>
        )}

        {step === 3 && (
          <Stack spacing={1}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>分割後の伝票プレビュー</Typography>
            {Array.from({ length: splitCount }, (_, t) => {
              const qty = rows.reduce((s, r) => s + (r.alloc[t]?.quantity || 0), 0);
              const wt = rows.reduce((s, r) => s + (r.alloc[t]?.weightG || 0), 0);
              const vol = rows.reduce((s, r) => s + (r.alloc[t]?.volumeMm3 ?? 0), 0);
              const prods = rows
                .filter((r) => (r.alloc[t]?.quantity || 0) > 0 || (r.alloc[t]?.weightG || 0) > 0)
                .map((r) => r.name);
              const title = `分割伝票 ${t + 1}`;
              return (
                <Box key={`preview-${t + 1}`} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }} display="block">
                        商品: {prods.join(', ') || '（割当なし）'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      数量 {qty} / 重量 {gToKg(wt)}kg / 体積 {toM3(vol)}m³
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Box sx={{ flexGrow: 1 }} />
        {step > 1 && <Button onClick={() => setStep((s) => (s - 1) as Step)}>戻る</Button>}
        {step < 3 && (
          <Button
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            disabled={(step === 1 && (splitCount < MIN_SPLIT || splitCount > MAX_SPLIT)) || (step === 2 && !confirmable)}
            onClick={() => setStep((s) => (s + 1) as Step)}
          >
            次へ
          </Button>
        )}
        {step === 3 && (
          <Button variant="contained" disabled={!confirmable || loading} onClick={handleConfirm}>
            分割を確定
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

/** 体積(mm³)を m³ 表示用に変換し、重量(kg)と同じく小数3桁へ丸める */
const toM3 = (mm3: number | null): number | null => {
  const v = mm3ToM3(mm3);
  return v === null ? null : Math.round(v * 1000) / 1000;
};

/** Step1 の伝票情報行（ラベル左・値右） */
const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
  </Stack>
);

/** Step2 テーブルのヘッダーセル */
const Th = ({ children, sx }: { children?: ReactNode; sx?: object }) => (
  <Box
    component="th"
    sx={{
      px: 1, py: 0.75,
      fontSize: 11, fontWeight: 700,
      textAlign: 'center', color: 'text.secondary',
      borderRight: '1px solid', borderColor: 'divider',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/** Step2 テーブルのデータセル */
const Td = ({ children, sx }: { children?: ReactNode; sx?: object }) => (
  <Box
    component="td"
    sx={{
      px: 1, py: 1,
      borderRight: '1px solid', borderColor: 'divider',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/** Step2 各セル内の数値入力（左ラベル + compact TextField） */
const NumInput = ({
  label, value, onChange, step = 1, disabled,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  disabled?: boolean;
}) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <Typography sx={{ fontSize: 11, width: 20, color: 'text.secondary' }}>{label}</Typography>
    <TextField
      type="number"
      size="small"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
      slotProps={{ htmlInput: { min: 0, step, style: { padding: '6px 8px', fontSize: 13, width: 96, textAlign: 'right' } } }}
    />
  </Stack>
);

/** Step2 残量テキスト */
const RemText = ({ label, value, isError }: { label: string; value: number | null; isError: boolean }) => {
  const isNonZero = value !== null && Math.abs(value) > 1e-6;
  return (
    <Typography sx={{ fontSize: 10, fontWeight: isNonZero ? 700 : 500, color: isError ? 'error.main' : 'text.secondary' }}>
      {label}: {value}
    </Typography>
  );
};

export default ShipmentOrderLegRunSplitForm;
